import { Canvas } from '@react-three/fiber';
import { Suspense, useState, useEffect, useRef } from 'react';
import NeuralNetwork from '../three/NeuralNetwork';

const About = () => {
  const skills = [
    { name: 'AI/ML Systems', level: 95 },
    { name: 'Quantum Computing', level: 90 },
    { name: 'Cybersecurity', level: 90 },
    { name: 'Cloud Architecture', level: 85 },
    { name: 'Full-Stack Development', level: 85 },
    { name: 'High-Performance Computing', level: 80 },
  ];

  const [isVisible, setIsVisible] = useState(false);
  const canvasRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin: '100px' }
    );

    if (canvasRef.current) {
      observer.observe(canvasRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section id="about" className="relative section-padding bg-bg-darker">
      <div className="container-custom">
        <div className="grid lg:grid-cols-2 gap-6 md:gap-12 items-center">
          {/* Content */}
          <div className="z-10">
            <h2 className="text-3xl md:text-5xl font-heading font-bold mb-4 md:mb-6">
              About <span className="gradient-text">Me</span>
            </h2>

            <p className="text-sm md:text-lg text-text-secondary mb-4 md:mb-6 leading-relaxed">
              I'm a software engineer specializing in{' '}
              <span className="text-quantum-blue font-semibold">AI/ML systems</span>,{' '}
              <span className="text-quantum-purple font-semibold">quantum computing</span>, and{' '}
              <span className="text-quantum-green font-semibold">cybersecurity</span>. With 7
              years of experience spanning embedded systems to cloud-scale ML pipelines, I bring
              both depth and breadth to complex technical challenges.
            </p>

            <p className="text-sm md:text-lg text-text-secondary mb-4 md:mb-6 leading-relaxed">
              Currently pursuing{' '}
              <span className="text-highlight-marker">
                M.Res. in Quantum Computing at Capitol Technology University
              </span>{' '}
              (Expected Spring 2027).
            </p>

            <div className="mb-6 md:mb-8">
              <h3 className="text-xl md:text-2xl font-heading font-semibold mb-3 md:mb-4 text-quantum-blue">
                Key Expertise
              </h3>
              <div className="space-y-3 md:space-y-4">
                {skills.map((skill) => (
                  <div key={skill.name}>
                    <div className="flex justify-between mb-1 md:mb-2">
                      <span className="text-text-primary font-medium text-sm md:text-base">{skill.name}</span>
                      <span className="text-text-secondary text-sm">{skill.level}%</span>
                    </div>
                    <div className="h-1.5 md:h-2 bg-bg-dark rounded-full overflow-hidden">
                      <div
                        className="h-full bg-gradient-to-r from-quantum-blue to-quantum-green transition-all duration-1000"
                        style={{ width: `${skill.level}%` }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex flex-wrap gap-2 md:gap-3">
              <span className="px-3 py-1.5 md:px-4 md:py-2 bg-quantum-blue/20 border border-quantum-blue rounded-lg text-quantum-blue text-xs md:text-sm">
                AWS Braket Certified
              </span>
              <span className="px-3 py-1.5 md:px-4 md:py-2 bg-quantum-green/20 border border-quantum-green rounded-lg text-quantum-green text-xs md:text-sm">
                CompTIA Security+
              </span>
              <span className="px-3 py-1.5 md:px-4 md:py-2 bg-quantum-purple/20 border border-quantum-purple rounded-lg text-quantum-purple text-xs md:text-sm">
                ML Specialization
              </span>
            </div>
          </div>

          {/* 3D Neural Network Visualization - Hidden on mobile */}
          <div ref={canvasRef} className="hidden lg:block relative h-[500px] rounded-xl overflow-hidden glass">
            {isVisible && (
              <Canvas camera={{ position: [0, 0, 10], fov: 75 }}>
                <Suspense fallback={null}>
                  <NeuralNetwork />
                </Suspense>
              </Canvas>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default About;
